import React from 'react';
import UWSection from 'modules/cm/components/UWSection/UWSection';
import UWGrid from 'modules/cm/components/UWGrid/UWGrid';
import UWTable from 'modules/cm/components/UWTable/UWTable';
import { FIELDS, SUBDOMAINS } from 'modules/cm/domain/Underwriting/Underwriting';
import {
  mapAffordabilityToViewModel,
  mapApprovalsToViewModel,
  mapAssetsToViewModel,
  mapBorrowerToViewModel,
  mapCreditRecordCommonDataToViewModel,
  mapCreditRecordToViewModel,
  mapCurrentIncomeToViewModel,
  mapExpenditureCommonDataToViewModels,
  mapExpenditureToViewModels,
  mapFutureIncomeToViewModel,
  mapLoanToViewModel,
  mapMortgagePropertyToViewModel,
  mapRepaymentStrategyToViewModel,
  mapSpecialConditionsToViewModel,
  mapSummaryToViewModel,
  mapSurvivorIncomeToViewModel,
} from './viewModel/mappers';
import useCurrentCaseStore from 'modules/cm/hooks/useCurrentCaseStore';
import { CaseDetails } from '../../CaseDetailsPage.types';
import { RepaymentStrategy } from '../../../../RepaymentStrategy/RepaymentStrategy';

const UnderwritingSummary = () => {
  const data = useCurrentCaseStore().underwriting;
  const repaymentStrategy = (useCurrentCaseStore().data as CaseDetails).case.repaymentStrategy as RepaymentStrategy;
  data['repaymentStrategy'] = { ...repaymentStrategy };

  return (
    <UWGrid>
      <UWSection title="Summary">
        <UWTable titles={[]} rows={mapSummaryToViewModel(data[SUBDOMAINS.SUMMARY])} />
      </UWSection>
      <UWSection title="Loan">
        <UWTable titles={[]} rows={mapLoanToViewModel(data[SUBDOMAINS.LOAN])} />
      </UWSection>
      <UWSection title="Borrower">
        <UWTable titles={['', 'Applicant 1', 'Applicant 2']} rows={mapBorrowerToViewModel(data[SUBDOMAINS.BORROWER])} />
      </UWSection>
      <UWSection title="Credit Record">
        <UWTable titles={[]} rows={mapCreditRecordCommonDataToViewModel(data[SUBDOMAINS.CREDIT_RECORD])} />
        <UWTable
          title="Applicant 1"
          titles={['', 'Value', 'Date for latest']}
          rows={mapCreditRecordToViewModel(data[SUBDOMAINS.CREDIT_RECORD][FIELDS.APPLICANT_1])}
        />
        {data[SUBDOMAINS.CREDIT_RECORD][FIELDS.APPLICANT_2] && (
          <UWTable
            title="Applicant 2"
            titles={['', 'Value', 'Date for latest']}
            rows={mapCreditRecordToViewModel(data[SUBDOMAINS.CREDIT_RECORD][FIELDS.APPLICANT_2])}
          />
        )}
      </UWSection>
      <UWSection title="Mortgaged Property">
        <UWTable titles={[]} rows={mapMortgagePropertyToViewModel(data[SUBDOMAINS.MORTGAGE_PROPERTY])} />
      </UWSection>
      <UWSection title="Income">
        <UWTable
          title="Current Income"
          titles={['', 'Applicant 1', 'Applicant 2']}
          rows={mapCurrentIncomeToViewModel(data[SUBDOMAINS.INCOME][FIELDS.CURRENT_INCOME])}
        />
        <UWTable
          title="Future Income"
          titles={['', 'Applicant 1', 'Applicant 2']}
          rows={mapFutureIncomeToViewModel(data[SUBDOMAINS.INCOME][FIELDS.FUTURE_INCOME])}
        />
        {data[SUBDOMAINS.INCOME]['current'].applicant2 && (
          <UWTable
            title="Survivor Income"
            titles={['', 'Applicant 1', 'Applicant 2']}
            rows={mapSurvivorIncomeToViewModel(data[SUBDOMAINS.INCOME][FIELDS.SURVIVOR])}
          />
        )}
      </UWSection>
      <UWSection title="Assets">
        <UWTable
          title="Applicant 1"
          titles={['Type', 'Value', 'Description']}
          rows={mapAssetsToViewModel(data[SUBDOMAINS.ASSETS][FIELDS.APPLICANT_1])}
        />
        {data[SUBDOMAINS.ASSETS][FIELDS.APPLICANT_2] && (
          <UWTable
            title="Applicant 2"
            titles={['Type', 'Value', 'Description']}
            rows={mapAssetsToViewModel(data[SUBDOMAINS.ASSETS][FIELDS.APPLICANT_2])}
          />
        )}
      </UWSection>
      <UWSection title="Expenditure">
        <UWTable titles={[]} rows={mapExpenditureCommonDataToViewModels(data[SUBDOMAINS.EXPENDITURE])} />
        {mapExpenditureToViewModels(data[SUBDOMAINS.EXPENDITURE]).map((viewModels, index) => (
          <UWTable
            key={`expenditure-applicant-${index + 1}`}
            title={`Applicant ${index + 1}`}
            titles={['', 'Monthly', 'Balance']}
            rows={viewModels}
          />
        ))}
      </UWSection>
      <UWSection title="Affordability Outputs">
        <UWTable titles={[]} rows={mapAffordabilityToViewModel(data[SUBDOMAINS.AFFORDABILITY])} />
      </UWSection>
      <UWSection title="Special conditions">
        <UWTable titles={[]} rows={mapSpecialConditionsToViewModel(data[SUBDOMAINS.SPECIAL_CONDITIONS])} />
      </UWSection>
      <UWSection title="Approvals">
        <UWTable titles={[]} rows={mapApprovalsToViewModel(data[SUBDOMAINS.APPROVALS])} />
      </UWSection>
      <UWSection title="Repayment Strategy">
        <UWTable
          titles={[]}
          rows={mapRepaymentStrategyToViewModel({
            productType: data.loan[FIELDS.PRODUCT_TYPE],
            ...data[SUBDOMAINS.REPAYMENT_STRATEGY],
          })}
        />
      </UWSection>
    </UWGrid>
  );
};

export default UnderwritingSummary;
